import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { getEnv } from '../../lib/env';

interface Company {
  id: string;
  name: string;
  slug: string;
  tenant_id: string;
}

interface ImportStats {
  company_id: string;
  company_name: string;
  existing_count: number;
  successware_total: number;
  remaining_count: number;
  last_import_date: string | null;
}

interface ImportProgress {
  company_name: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  processed: number;
  successful: number;
  failed: number;
  skipped: number;
  error_message?: string;
}

export default function SuccessWareIncrementalImportPage() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [stats, setStats] = useState<ImportStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<ImportProgress[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const env = getEnv();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load companies
      const { data: companiesData, error: companiesError } = await supabase
        .from('company_configs')
        .select('id, name, slug, tenant_id')
        .eq('is_active', true)
        .order('name');

      if (companiesError) {
        console.error('Error loading companies:', companiesError);
        alert('Failed to load companies. Please check console for details.');
        return;
      }

      if (companiesData && companiesData.length > 0) {
        setCompanies(companiesData);

        // Load stats for each company
        const statsPromises = companiesData.map(async (company) => {
          try {
            // Get existing customer count from per-company table
            const tableName = `${company.slug}_customers`;
            const { count: existingCount, error: countError } = await supabase
              .from(tableName)
              .select('*', { count: 'exact', head: true });

            if (countError) {
              console.error(`Error counting customers for ${company.name}:`, countError);
            }

            // Get last import date
            const { data: lastBatch } = await supabase
              .from('sw_import_batches')
              .select('completed_at')
              .eq('company_id', company.id)
              .eq('import_type', 'customers')
              .order('completed_at', { ascending: false })
              .limit(1)
              .maybeSingle();

            return {
              company_id: company.id,
              company_name: company.name,
              existing_count: existingCount || 0,
              successware_total: 0, // Will be fetched from SuccessWare
              remaining_count: 0,
              last_import_date: lastBatch?.completed_at || null
            };
          } catch (err) {
            console.error(`Error loading stats for ${company.name}:`, err);
            return {
              company_id: company.id,
              company_name: company.name,
              existing_count: 0,
              successware_total: 0,
              remaining_count: 0,
              last_import_date: null
            };
          }
        });

        const statsData = await Promise.all(statsPromises);
        setStats(statsData);
      } else {
        console.warn('No active companies found in company_configs table');
        alert('No active companies found. Please check your database configuration.');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      alert(`Error loading data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleCompanySelection = (companyId: string) => {
    setSelectedCompanies(prev => 
      prev.includes(companyId) 
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    );
  };

  const selectAllCompanies = () => {
    setSelectedCompanies(companies.map(c => c.id));
  };

  const deselectAllCompanies = () => {
    setSelectedCompanies([]);
  };

  const startIncrementalImport = async () => {
    if (selectedCompanies.length === 0) {
      alert('Please select at least one company');
      return;
    }

    if (!confirm(`Start incremental import for ${selectedCompanies.length} compan${selectedCompanies.length === 1 ? 'y' : 'ies'}?\n\nThis will only import NEW customers not already in the database.`)) {
      return;
    }

    setImporting(true);
    setProgress([]);

    try {
      // Initialize progress for each selected company
      const initialProgress = selectedCompanies.map(companyId => {
        const company = companies.find(c => c.id === companyId);
        return {
          company_name: company?.name || 'Unknown',
          status: 'pending' as const,
          processed: 0,
          successful: 0,
          failed: 0,
          skipped: 0
        };
      });
      setProgress(initialProgress);

      // Process each company sequentially
      for (let i = 0; i < selectedCompanies.length; i++) {
        const companyId = selectedCompanies[i];

        // Update status to processing
        setProgress(prev => prev.map((p, idx) => 
          idx === i ? { ...p, status: 'processing' } : p
        ));

        try {
          // Call the new incremental import edge function
          const response = await fetch(
            `${env.supabaseUrl}/functions/v1/successware-incremental-import`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${env.supabaseAnonKey}`
              },
              body: JSON.stringify({
                company_id: companyId,
                batch_size: 100
              })
            }
          );

          const result = await response.json();

          if (result.success) {
            // Update progress with results
            setProgress(prev => prev.map((p, idx) => 
              idx === i ? {
                ...p,
                status: 'completed',
                processed: result.total_processed || 0,
                successful: result.imported_count || 0,
                skipped: result.skipped_count || 0,
                failed: result.failed_count || 0
              } : p
            ));
          } else {
            throw new Error(result.error || 'Import failed');
          }
        } catch (error: any) {
          // Update progress with error
          setProgress(prev => prev.map((p, idx) => 
            idx === i ? {
              ...p,
              status: 'error',
              error_message: error.message
            } : p
          ));
        }

        // Wait 2 seconds between companies
        if (i < selectedCompanies.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }

      // Reload stats after import
      await loadData();
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading import data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">SuccessWare Incremental Import</h1>
        <p className="text-gray-600 mt-1">
          Import remaining customers from SuccessWare without creating duplicates
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h3 className="text-sm font-medium text-blue-900">How Incremental Import Works</h3>
            <p className="mt-1 text-sm text-blue-800">
              This import checks existing customers using their SuccessWare Service Account ID and Company ID. 
              Only customers that don't already exist will be imported. This prevents duplicates and allows you 
              to safely run the import multiple times.
            </p>
          </div>
        </div>
      </div>

      {/* Company Selection */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Select Companies to Import</h2>
            <div className="space-x-2">
              <button
                onClick={selectAllCompanies}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Select All
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={deselectAllCompanies}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Deselect All
              </button>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {stats.map((stat) => (
              <div
                key={stat.company_id}
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                  selectedCompanies.includes(stat.company_id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => toggleCompanySelection(stat.company_id)}
              >
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    checked={selectedCompanies.includes(stat.company_id)}
                    onChange={() => toggleCompanySelection(stat.company_id)}
                    className="mt-1 h-4 w-4 text-blue-600 rounded"
                  />
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-gray-900">{stat.company_name}</h3>
                    <div className="mt-2 grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Existing Customers:</span>{' '}
                        {stat.existing_count.toLocaleString()}
                      </div>
                      {stat.last_import_date && (
                        <div>
                          <span className="font-medium">Last Import:</span>{' '}
                          {new Date(stat.last_import_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Import Button */}
      <div className="mb-6">
        <button
          onClick={startIncrementalImport}
          disabled={importing || selectedCompanies.length === 0}
          className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
        >
          {importing ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Importing...
            </span>
          ) : (
            `Start Incremental Import (${selectedCompanies.length} ${selectedCompanies.length === 1 ? 'Company' : 'Companies'})`
          )}
        </button>
      </div>

      {/* Progress Display */}
      {progress.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Import Progress</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {progress.map((p, idx) => (
                <div key={idx} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-900">{p.company_name}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      p.status === 'completed' ? 'bg-green-100 text-green-800' :
                      p.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                      p.status === 'error' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                    </span>
                  </div>
                  {p.status === 'processing' && (
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '50%' }}></div>
                    </div>
                  )}
                  {(p.status === 'completed' || p.status === 'error') && (
                    <div className="grid grid-cols-4 gap-2 text-sm text-gray-600 mt-2">
                      <div>
                        <span className="font-medium">Processed:</span> {p.processed}
                      </div>
                      <div>
                        <span className="font-medium text-green-600">Imported:</span> {p.successful}
                      </div>
                      <div>
                        <span className="font-medium text-yellow-600">Skipped:</span> {p.skipped}
                      </div>
                      <div>
                        <span className="font-medium text-red-600">Failed:</span> {p.failed}
                      </div>
                    </div>
                  )}
                  {p.error_message && (
                    <div className="mt-2 text-sm text-red-600">
                      Error: {p.error_message}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
